// Score display normalization (#993).
//
// The app mixes two score conventions that historically collided in the
// display layer:
//
//   * 0-1 FRACTIONS  — the ML model outputs (mineral_score, value_score,
//     geothermal_score, opportunity_score, commodity *_prob). These are
//     rendered as a percentage by multiplying by 100.
//
//   * 0-100 SCORES   — the geophysical feasibility scores (solar_score,
//     wind_score, wind_score_0_100, hydro_score, hydro_score_0_100) and
//     the FEMA hazard composites. These are ALREADY on a 0-100 axis.
//
// The <ScoreBar> primitive assumed every value was a 0-1 fraction and did
// `value * 100`, so a solar_score of 85 rendered as "8500%" (issue #993).
// `toDisplayPercent` collapses both conventions onto one 0-100 axis:
// values in [0, 1] are treated as fractions and scaled up; values above 1
// are treated as already-0-100 scores and passed through (clamped).
//
// Auto-detection is safe because the 0-100 feasibility scores are clipped
// to [0, 100] upstream and realistic values sit in the tens; a genuine
// 0-1 fraction of exactly 1.0 maps to 100 under either interpretation.
// Callers that know their scale can pass it explicitly to remove any
// ambiguity.

/**
 * Normalize a score to a 0-100 display number.
 *
 * @param {number|null|undefined} value - raw score (0-1 fraction OR 0-100)
 * @param {('auto'|'fraction'|'0-100')} [scale='auto'] - how to interpret value
 * @returns {number|null} 0-100 number, or null when value is missing
 */
export function toDisplayPercent(value, scale = 'auto') {
  if (value == null || Number.isNaN(value)) return null;
  const x = Number(value);
  let pct;
  if (scale === 'fraction') {
    pct = x * 100;
  } else if (scale === '0-100') {
    pct = x;
  } else {
    // auto: <= 1 is a fraction, > 1 is already 0-100.
    pct = x <= 1 ? x * 100 : x;
  }
  // Clamp so a stray out-of-range value never blows out a progress bar.
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

/**
 * Format a score as a rounded integer percent string (e.g. "72%").
 * Missing values render as an em dash.
 *
 * @param {number|null|undefined} value
 * @param {('auto'|'fraction'|'0-100')} [scale='auto']
 * @returns {string}
 */
export function formatScorePercent(value, scale = 'auto') {
  const pct = toDisplayPercent(value, scale);
  if (pct == null) return '—';
  return `${Math.round(pct)}%`;
}

// NOTE (#1017): the client-side `rescaleToReference` min-max hack and its
// `OPPORTUNITY_REFERENCE_MAX = 0.23` fallback constant were removed once the
// backend began emitting every score already normalized to a 0-100
// percentile. Callers now pass `scale='0-100'` to `toDisplayPercent` /
// `formatScorePercent` and render the value directly.
